import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import {
  Alert, Button, Grid, CircularProgress, Box, Paper, Typography,
  Divider, TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText
} from '@mui/material';

import { useQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import CommentIcon from '@mui/icons-material/Comment';
import LoginIcon from '@mui/icons-material/Login';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { dispatchException, dispatchMessage } from 'helper/snackbar';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputDropdown, FormInputCountryDropdown, FormInputSubdivisionDropdown } from 'components/form/FormInputDropdown';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import { userSelector } from 'helper/security';
import ClientCommentsSection from '../../components/comments/ClientCommentsSection';

import {
  PUBLIC_COMPANY_LIST_QUERY,
  UPDATE_PUBLIC_CLIENT_MUTATION,
  CREATE_PUBLIC_CLIENT_MUTATION,
  VIEW_PUBLIC_CLIENT_QUERY,
  SUDO_CLIENT_MUTATION,
  VIEW_PUBLIC_COMPANY_QUERY
} from './gql';
import { HL_PublicClientOutput, EClientGender } from '__generated__/graphql';
import yup from 'validation';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import { openLinkInNewTab } from 'helper/link';
import { getPhoneCountryOptions } from 'semshared/utils/phoneCountryCodes';
import ContactTasksList from '../../components/ContactTasksList';
import TasksIcon from '@mui/icons-material/Task';

interface PublicClientFormProps {
  data: HL_PublicClientOutput;
  isNew?: boolean;
  companyData?: any;
}

const validationSchema = yup.object().shape({
  id: yup.number().required(),
  firstname: yup.string().required().label('publicclient-firstname'),
  lastname: yup.string().required().label('publicclient-lastname'),
  email: yup.string().email().required().label('publicclient-email'),
  phone: yup.string().label('publicclient-phone'),
  mobile: yup.string().label('publicclient-mobile'),
  phoneCountryCode: yup.string().label('publicclient-phone-country-code'),
  mobileCountryCode: yup.string().label('publicclient-mobile-country-code'),
  publicCompanyId: yup.number().required().label('publicclient-company'),
  title: yup.string().label('publicclient-title'),
  position: yup.string().label('publicclient-position'),
  responsibility: yup.string().label('publicclient-responsibility'),
  mainLanguage: yup.string().label('publicclient-main-language'),
  gender: yup.mixed<EClientGender>().oneOf(Object.values(EClientGender)).nullable().label('publicclient-gender'),
  hasAddress: yup.boolean().default(false),
  address: yup.string().when('hasAddress', {
    is: true,
    then: () => yup.string().required().label('publicclient-street'),
    otherwise: () => yup.string().label('publicclient-street'),
  }),
  zip: yup.string().when('hasAddress', {
    is: true,
    then: () => yup.string().required().label('publicclient-zip'),
    otherwise: () => yup.string().label('publicclient-zip'),
  }),
  city: yup.string().when('hasAddress', {
    is: true,
    then: () => yup.string().required().label('publicclient-city'),
    otherwise: () => yup.string().label('publicclient-city'),
  }),
  country: yup.string().when('hasAddress', {
    is: true,
    then: () => yup.string().required().label('publicclient-country'),
    otherwise: () => yup.string().label('publicclient-country'),
  }),
  state: yup.string().label('publicclient-state'),
  addressSupplement: yup.string().label('publicclient-address-supplement'),
  isMaster: yup.boolean().label('publicclient-is-master'),
});

function PublicClientForm(props: PublicClientFormProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = userSelector()!;
  const [updateMutateFunction] = useMutation(UPDATE_PUBLIC_CLIENT_MUTATION);
  const [createMutateFunction] = useMutation(CREATE_PUBLIC_CLIENT_MUTATION);
  const [sudoClientMutation] = useMutation(SUDO_CLIENT_MUTATION);

  const { data: dataCompanies } = useQuery(PUBLIC_COMPANY_LIST_QUERY);

  // Get company name for display in new client form
  const selectedCompany = dataCompanies?.listPublicCompanies?.companies?.find(c => c.id === props.data.publicCompanyId);

  const [createdId, setCreatedId] = useState(0);
  useEffect(() => {
    if (createdId > 0) navigate(`/clients/clients/${createdId}`);
  }, [createdId, navigate]);

  type PublicClientFormType = yup.InferType<typeof validationSchema>;

  const toFormSchema = (obj: Partial<HL_PublicClientOutput>): PublicClientFormType => ({
    id: obj.id || 0,
    firstname: obj.firstname || '',
    lastname: obj.lastname || '',
    email: obj.email || '',
    phone: obj.phone || '',
    mobile: obj.mobile || '',
    phoneCountryCode: obj.phoneCountryCode || '+49',
    mobileCountryCode: obj.mobileCountryCode || '+49',
    publicCompanyId: obj.publicCompanyId || 0,
    title: obj.title || '',
    position: obj.position || '',
    responsibility: obj.responsibility || '',
    mainLanguage: obj.mainLanguage || 'DE',
    gender: (obj.gender as EClientGender) || null,
    hasAddress: Boolean((obj as any).hasAddress),
    address: obj.address || '',
    zip: obj.zip || '',
    city: obj.city || '',
    country: (obj.country as string) || 'DE',
    state: obj.state || '',
    addressSupplement: obj.addressSupplement || '',
    isMaster: obj.isMaster || false,
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    watch,
    setValue,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: { client: useApolloClient() },
    defaultValues: toFormSchema(props.data || {}),
  });

  const hasAddress = watch('hasAddress');
  const publicCompanyId = watch('publicCompanyId');

  const { data: fetchedCompanyData } = useQuery(VIEW_PUBLIC_COMPANY_QUERY, {
    variables: { id: publicCompanyId },
    skip: !publicCompanyId || !!props.companyData,
  });

  const activeCompanyData = props.companyData || fetchedCompanyData?.viewPublicCompany;
  const isSelectedCompanyActive = activeCompanyData?.isActive !== false;

  useEffect(() => {
    if (!hasAddress && activeCompanyData) {
      setValue('address', activeCompanyData.address || '');
      setValue('zip', activeCompanyData.zip || '');
      setValue('city', activeCompanyData.city || '');
      setValue('country', activeCompanyData.country || 'AT');
      setValue('state', activeCompanyData.state || '');
      setValue('addressSupplement', activeCompanyData.addressSupplement || '');
    }
  }, [hasAddress, activeCompanyData, props.isNew, setValue]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  const onSubmit = async (values: PublicClientFormType) => {
    try {
      const inputData = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        phone: values.phone,
        mobile: values.mobile,
        phoneCountryCode: values.phoneCountryCode,
        mobileCountryCode: values.mobileCountryCode,
        publicCompanyId: values.publicCompanyId,
        title: values.title,
        position: values.position,
        responsibility: values.responsibility,
        mainLanguage: values.mainLanguage,
        gender: values.gender,
        hasAddress: values.hasAddress,
        address: values.address,
        zip: values.zip,
        city: values.city,
        country: values.country,
        state: values.state,
        addressSupplement: values.addressSupplement,
        isMaster: values.isMaster,
      };

      if (props.data.id > 0) {
        const result = await updateMutateFunction({
          variables: {
            id: props.data.id,
            data: inputData,
          },
          update: (cache) => {
            // Invalidate the clients list cache to force refetch
            cache.evict({
              fieldName: 'listPublicClients'
            });
            cache.gc();
          },
          refetchQueries: ['ViewPublicClient'],
          awaitRefetchQueries: true,
        });
        // Reset form with updated data after successful save
        if (result.data?.updatePublicClient) {
          reset(toFormSchema(result.data.updatePublicClient));
        }
      } else {
        const res = await createMutateFunction({
          variables: {
            data: inputData,
          },
          update: (cache) => {
            // Completely clear all client-related cache
            cache.evict({ fieldName: 'listPublicClients' });
            cache.evict({ fieldName: 'listPublicCompanies' });

            // Clear all ROOT_QUERY entries that contain listPublicClients
            const rootQuery = cache.extract()?.ROOT_QUERY;
            if (rootQuery) {
              Object.keys(rootQuery).forEach(key => {
                if (key.includes('listPublicClients')) {
                  cache.evict({ id: 'ROOT_QUERY', fieldName: key });
                }
              });
            }

            cache.gc();
          },
        });
        if (res.data?.createPublicClient?.id) {
          // Add small delay to ensure cache is cleared before navigation
          setTimeout(() => {
            if (inputData.publicCompanyId) {
              navigate(`/clients/companies/${inputData.publicCompanyId}/contacts`);
            } else if (res.data?.createPublicClient?.id) {
              setCreatedId(res.data.createPublicClient.id);
            }
          }, 100);
        }
      }
      dispatchMessage(dispatch, i18next.t('publicclient-saved'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const phoneCountryOptions = getPhoneCountryOptions(i18next.language || 'en');

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([
          'Kunden',
          props.data.publicCompany?.name || null,
          props.isNew ? i18next.t('publicclient-new-title') : `${props.data.firstname} ${props.data.lastname}`
        ])}</title>
      </Helmet>
      <Box sx={{ mb: 2 }}>
        <Button
          component={Link}
          to={props.data.publicCompanyId ? `/clients/companies/${props.data.publicCompanyId}/contacts` : '/clients/companies'}
          startIcon={<ArrowBackIcon />}
          sx={{ textDecoration: 'none' }}
        >
          {i18next.t('publicclient-back-to-company')}
        </Button>
      </Box>

      {props.isNew && !isSelectedCompanyActive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {i18next.t('publiccompany-inactive-contact-create-blocked')}
        </Alert>
      )}

      <Grid container spacing={3}>
        <UnsavedChangesPrompt isDirty={isDirty && !props.isNew} />

        {/* Pflichtfelder */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PersonIcon sx={{ mr: 1 }} />
              {i18next.t('publicclient-section-required')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormInputText name="firstname" control={control} label={i18next.t('publicclient-firstname')} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormInputText name="lastname" control={control} label={i18next.t('publicclient-lastname')} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormInputText name="email" control={control} label={i18next.t('publicclient-email')} textFieldProps={{ type: 'email' }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* Show company as disabled field if it's not new OR if it's new with a company already selected */}
                {!props.isNew || (props.isNew && props.data.publicCompanyId > 0) ? (
                  <FormInputText
                    name="publicCompanyId"
                    control={control}
                    label={i18next.t('publicclient-company')}
                    textFieldProps={{
                      disabled: true,
                      value: props.data.publicCompany?.name || selectedCompany?.name || '',
                    }}
                  />
                ) : (
                  <FormInputDropdown
                    name="publicCompanyId"
                    control={control}
                    label={i18next.t('publicclient-company')}
                    options={(dataCompanies?.listPublicCompanies?.companies || []).filter(c => c.isActive).map(c => ({ value: c.id, label: c.name }))}
                    required
                  />
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Optionale persönliche Informationen */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BusinessIcon sx={{ mr: 1 }} />
              {i18next.t('publicclient-section-optional')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormInputText name="title" control={control} label={i18next.t('publicclient-title')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormInputText name="position" control={control} label={i18next.t('publicclient-position')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormInputText name="responsibility" control={control} label={i18next.t('publicclient-responsibility')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormInputDropdown
                  name="mainLanguage"
                  control={control}
                  label={i18next.t('publicclient-main-language')}
                  options={[
                    { value: 'DE', label: i18next.t('publicclient-language-german') },
                    { value: 'EN', label: i18next.t('publicclient-language-english') },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormInputDropdown
                  name="gender"
                  control={control}
                  label={i18next.t('publicclient-gender')}
                  options={[
                    { value: null, label: i18next.t('publicclient-no-selection') },
                    { value: EClientGender.MALE, label: i18next.t('publicclient-gender-male') },
                    { value: EClientGender.FEMALE, label: i18next.t('publicclient-gender-female') },
                    { value: EClientGender.VARIOUS, label: i18next.t('publicclient-gender-various') },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormInputCheckbox name="isMaster" control={control} label={i18next.t('publicclient-is-master')} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Kontaktinformationen */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ContactMailIcon sx={{ mr: 1 }} />
              {i18next.t('publicclient-section-contact')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18next.t('publicclient-section-phone')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormInputDropdown
                  name="phoneCountryCode"
                  control={control}
                  label={i18next.t('publicclient-phone-country-code')}
                  options={phoneCountryOptions}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormInputText name="phone" control={control} label={i18next.t('publicclient-phone')} />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18next.t('publicclient-section-mobile')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormInputDropdown
                  name="mobileCountryCode"
                  control={control}
                  label={i18next.t('publicclient-mobile-country-code')}
                  options={phoneCountryOptions}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormInputText name="mobile" control={control} label={i18next.t('publicclient-mobile')} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Adresse */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LocationOnIcon sx={{ mr: 1 }} />
              {i18next.t('publicclient-section-address')}
            </Typography>

            <Grid container spacing={2}>
              {/* Address Type Toggle */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!validationErrors.hasAddress}>
                  <InputLabel>{i18next.t('publicclient-address-type')}</InputLabel>
                  <Controller
                    name="hasAddress"
                    control={control}
                    render={({ field }) => {
                      const selectValue = field.value === true ? 'true' : 'false';

                      return (
                        <Select
                          value={selectValue}
                          onChange={e => {
                            const newValue = e.target.value === 'true';
                            field.onChange(newValue);
                          }}
                          disabled={!publicCompanyId}
                          label={i18next.t('publicclient-address-type')}
                        >
                          <MenuItem value="false">{i18next.t('publicclient-use-company-address')}</MenuItem>
                          <MenuItem value="true">{i18next.t('publicclient-use-own-address')}</MenuItem>
                        </Select>
                      );
                    }}
                  />
                  {validationErrors.hasAddress && <FormHelperText>{validationErrors.hasAddress.message}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Company Address Preview */}
              {!hasAddress && activeCompanyData && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', border: 1, borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" />
                      {i18next.t('publicclient-company-address-preview')}:
                    </Typography>
                    <Box sx={{ ml: 3 }}>
                      {activeCompanyData.name && (
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {activeCompanyData.name}
                        </Typography>
                      )}
                      {activeCompanyData.address && (
                        <Typography variant="body2">
                          {activeCompanyData.address}
                        </Typography>
                      )}
                      {activeCompanyData.addressSupplement && <Typography variant="body2">{activeCompanyData.addressSupplement}</Typography>}
                      {(activeCompanyData.zip || activeCompanyData.city) && (
                        <Typography variant="body2">
                          {activeCompanyData.zip} {activeCompanyData.city}
                        </Typography>
                      )}
                      {activeCompanyData.state && <Typography variant="body2">{activeCompanyData.state}</Typography>}
                      {activeCompanyData.country && <Typography variant="body2">{activeCompanyData.country}</Typography>}
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Own Address Fields */}
              {hasAddress && (
                <>
                  <Grid item xs={12} sm={8}>
                    <FormInputText name="address" control={control} label={i18next.t('publicclient-street')} required={hasAddress} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormInputText name="addressSupplement" control={control} label={i18next.t('publicclient-address-supplement')} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormInputText name="zip" control={control} label={i18next.t('publicclient-zip')} required={hasAddress} />
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <FormInputText name="city" control={control} label={i18next.t('publicclient-city')} required={hasAddress} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputCountryDropdown name="country" control={control} label={i18next.t('publicclient-country')} required={hasAddress} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputSubdivisionDropdown name="state" control={control} label={i18next.t('publicclient-state')} countryFieldName="country" />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Kommentare - nur für bestehende Kontakte */}
        {!props.isNew && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CommentIcon sx={{ mr: 1 }} />
                {i18next.t('publicclient-section-comments')}
              </Typography>
              <ClientCommentsSection
                clientId={props.data.id}
                companyId={props.data.publicCompanyId}
                clientName={`${props.data.firstname || ''} ${props.data.lastname || ''}`.trim()}
              />
            </Paper>
          </Grid>
        )}

        {/* Tasks - nur für bestehende Kontakte */}
        {!props.isNew && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TasksIcon sx={{ mr: 1 }} />
                Tasks
              </Typography>
              <ContactTasksList 
                publicClientId={props.data.id} 
                publicCompanyId={props.data.publicCompanyId}
              />
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          {props.data.id > 0 && user.isSeminargo && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={<LoginIcon />}
              confirmationQuestion={i18next.t('publicclient-confirm-sudo')}
              confirmationTitle={i18next.t('publicclient-confirm-sudo-title')}
              onConfirm={async () => {
                try {
                  const res = await sudoClientMutation({
                    variables: { publicClientId: props.data.id },
                  });
                  if (res.data?.sudoClient.success) {
                    openLinkInNewTab(res.data.sudoClient.href || '');
                  } else {
                    dispatchException(dispatch, `${i18next.t('publicclient-sudo-failed')}`);
                  }
                } catch (err) {
                  dispatchException(dispatch, err);
                }
              }}
            >
              {i18next.t('publicclient-sudo')}
            </ConfirmationButton>
          )}
          <Button
            sx={{ marginRight: 2 }}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating || (props.isNew && !isSelectedCompanyActive)}
            onClick={async () => {
              const valid = await trigger();
              if (valid) {
                handleSubmit(onSubmit)();
              }
            }}
          >
            {i18next.t('publicclient-save')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

function ClientDetailWrapper() {
  const { id } = useParams();
  const location = window.location;
  const urlParams = new URLSearchParams(location.search);
  const companyIdParam = urlParams.get('companyId');

  const companyQuery = useQuery(VIEW_PUBLIC_COMPANY_QUERY, {
    variables: { id: parseInt(companyIdParam!) },
    skip: !companyIdParam,
  });

  const clientQuery = useQuery(VIEW_PUBLIC_CLIENT_QUERY, {
    variables: { id: parseInt(id!) },
    skip: !id || id === 'new',
  });

  if (companyQuery.loading || clientQuery.loading) return <CircularProgress />;
  if (companyQuery.error || clientQuery.error) return <Alert severity="error">{companyQuery.error?.message || clientQuery.error?.message}</Alert>;

  if (!id || id === 'new') {
    // New client form with default values
    const defaultData: any = {
      id: 0,
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      mobile: '',
      phoneCountryCode: companyQuery.data?.viewPublicCompany?.phoneCountryCode || '+43',
      mobileCountryCode: companyQuery.data?.viewPublicCompany?.phoneCountryCode || '+43',
      publicCompanyId: companyIdParam ? parseInt(companyIdParam) : 0,
      title: '',
      position: '',
      responsibility: '',
      mainLanguage: 'DE',
      gender: null,
      hasAddress: false,
      address: '',
      zip: '',
      city: '',
      country: companyQuery.data?.viewPublicCompany?.country || 'AT',
      state: '',
      addressSupplement: '',
      isMaster: false,
    };
    return <PublicClientForm data={defaultData} isNew companyData={companyQuery.data?.viewPublicCompany} />;
  }
  else return <PublicClientForm data={clientQuery.data!.viewPublicClient as HL_PublicClientOutput} />;
}

/**
 * Client detail page wrapper that loads client and company data.
 * Renders client form for editing client information and comments.
 * Supports creation (no id param) and editing modes.
 */
export default ClientDetailWrapper;